import { ExclamationTriangleIcon } from "@storytell/bsky-icons";
import type { StreamingBlock } from "@storytell/streaming";
import { determinePreferredRenderer } from "@storytell/streaming";
import { Show } from "solid-js";

interface UnknownRendererProps {
  block: StreamingBlock;
  onBlockClick?: () => void;
}

/**
 * Fallback renderer for blocks with unknown or unspecified message types.
 * Provides basic display with a clear indication that the renderer is unknown.
 */
export const UnknownRenderer = (props: UnknownRendererProps) => {
  const content = () => props.block.deltas.map((delta) => delta.text).join("");

  return (
    <div
      data-renderer="unknown-renderer"
      class="border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 rounded-lg p-4"
    >
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0 mt-1">
          <ExclamationTriangleIcon size={16} class="text-amber-600 dark:text-amber-400" />
        </div>

        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">Unknown Block Type</div>

          <div class="text-xs text-amber-600 dark:text-amber-300 mb-3">
            MessageType: {props.block.messageType} | Renderer: {determinePreferredRenderer(props.block)}
          </div>

          <Show when={props.block.title}>
            <div class="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">{props.block.title}</div>
          </Show>

          <Show when={props.block.activity}>
            <div class="text-xs text-amber-600 dark:text-amber-400 mb-2 italic">Activity: {props.block.activity}</div>
          </Show>

          <Show when={content().trim()}>
            <div class="text-sm text-amber-700 dark:text-amber-200 bg-white dark:bg-amber-900 rounded border border-amber-200 dark:border-amber-800 p-3">
              <div class="whitespace-pre-wrap text-xs">{content()}</div>
            </div>
          </Show>

          <Show when={!props.block.isComplete}>
            <div class="flex items-center gap-2 mt-3">
              <div class="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              <span class="text-xs text-amber-600 dark:text-amber-300">Processing...</span>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
